import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getPhotoControlSchedules,
  getPhotoControlEntries,
  getPhotoControlEntryDetail,
  createPhotoControlSchedule,
  updatePhotoControlSchedule,
  deletePhotoControlSchedule,
  updatePhotoControlEntry,
  type CreatePhotoControlSchedulePayload,
  type UpdatePhotoControlSchedulePayload,
  type UpdatePhotoControlEntryPayload,
} from '../../../api/photo-control';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

async function invalidatePhotoControlQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['photo-control-schedules', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['photo-control-entries', patientId] }),
  ]);
}

export function usePhotoControlSchedules(patientId: string, treatmentId: string) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['photo-control-schedules', patientId, treatmentId],
    queryFn: () => getPhotoControlSchedules(patientId, treatmentId, getToken()),
    enabled: !!patientId && !!treatmentId,
  });
}

export function usePhotoControlEntries(
  patientId: string,
  params?: { status?: string },
) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: ['photo-control-entries', patientId, params?.status],
    queryFn: () => getPhotoControlEntries(patientId, getToken(), params),
    enabled: !!patientId,
  });
}

export function useCreatePhotoControlSchedule(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePhotoControlSchedulePayload) =>
      createPhotoControlSchedule(patientId, payload, getToken()),
    onSuccess: () => invalidatePhotoControlQueries(queryClient, patientId),
  });
}

export function useUpdatePhotoControlSchedule(patientId: string, scheduleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdatePhotoControlSchedulePayload) =>
      updatePhotoControlSchedule(patientId, scheduleId, payload, getToken()),
    onSuccess: () => invalidatePhotoControlQueries(queryClient, patientId),
  });
}

export function useDeletePhotoControlSchedule(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) =>
      deletePhotoControlSchedule(patientId, scheduleId, getToken()),
    onSuccess: () => invalidatePhotoControlQueries(queryClient, patientId),
  });
}

export function usePhotoControlEntryDetail(patientId: string, entryId: string | null) {
  const getToken = useRequireToken();
  return useQuery({
    queryKey: ['photo-control-entry', patientId, entryId],
    queryFn: () => getPhotoControlEntryDetail(patientId, entryId!, getToken()),
    enabled: !!patientId && !!entryId,
  });
}

export function useUpdatePhotoControlEntry(patientId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entryId, payload }: { entryId: string; payload: UpdatePhotoControlEntryPayload }) =>
      updatePhotoControlEntry(patientId, entryId, payload, getToken()),
    onSuccess: () => invalidatePhotoControlQueries(queryClient, patientId),
  });
}
